/**
 * A cannonball's flight over the board, in view space: shared by the arcs of fire shown on
 * hover and the shots themselves, so a ball flies the arc the player was shown.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Flight {
  /** Off the shooter's deck. */
  readonly from: Point;
  /** Into the target's hull, on the side facing the shooter. */
  readonly to: Point;
  /** Where the ball is at `t` (0–1) of its flight. */
  readonly at: (t: number) => Point;
}

/**
 * From a shooter standing at `a` to a target at `b` (keel midpoints): an arc rising over the
 * sea, higher the farther it goes, so even a neighbour is fired at over a hump. Null when the
 * two stand on one spot.
 */
export function flight(a: Point, b: Point): Flight | null {
  const p0 = { x: a.x, y: a.y - 6 };
  const span = Math.hypot(b.x - a.x, b.y - a.y);
  if (span < 1) return null;
  // Each ball falls on the side of the hull facing its shooter, so the heads do not pile up.
  const p2 = { x: b.x + ((a.x - b.x) / span) * 3, y: b.y - 5 + ((a.y - b.y) / span) * 3 };
  const dist = Math.hypot(p2.x - p0.x, p2.y - p0.y);
  if (dist < 1) return null;
  // The hump bulges along the chord's upper normal, tipped upwards: over a level shot it rises
  // straight up, a shot straight down the screen swings out to the side rather than fold.
  const rise = Math.max(14, Math.min(60, dist * 0.35));
  let [nx, ny] = [(p2.y - p0.y) / dist, -(p2.x - p0.x) / dist];
  if (ny > 0 || (ny === 0 && nx < 0)) [nx, ny] = [-nx, -ny];
  const [lx, ly] = [nx, ny - 0.6];
  const lift = rise / Math.hypot(lx, ly);
  const p1 = { x: (p0.x + p2.x) / 2 + lx * lift, y: (p0.y + p2.y) / 2 + ly * lift };
  return {
    from: p0,
    to: p2,
    at: (t) => ({
      x: (1 - t) ** 2 * p0.x + 2 * (1 - t) * t * p1.x + t * t * p2.x,
      y: (1 - t) ** 2 * p0.y + 2 * (1 - t) * t * p1.y + t * t * p2.y,
    }),
  };
}

/** In a volley, a shooter fires this long after the one before (gunfire.ts), ms. */
export const SHOT_STAGGER_MS = 140;
/** From a gun's flash to its ball leaving the smoke. */
export const SHOT_LAUNCH_MS = 70;
/** About how long a fired ball is in the air: between 840 and 1600 ms by how far it flies. */
const SHOT_FLIGHT_MS = 1300;

/** About when the last ball of a shot by `shooters` guns falls, ms after the shot. */
export function shotLandsAfter(shooters: number): number {
  return (Math.max(1, shooters) - 1) * SHOT_STAGGER_MS + SHOT_LAUNCH_MS + SHOT_FLIGHT_MS;
}

export function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
